import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';

/**
 * GlobalAlertProvider — overrides the browser's native `window.alert()` with a
 * centered themed dialog that matches the POS dark theme.
 *
 * Rationale: the codebase has ~80 `alert()` call sites. Intercepting globally
 * is the only pragmatic way to replace all of them at once without touching
 * every file. The tone (success / error / info) is inferred from common emoji
 * prefixes the existing code uses (✅ / ❌ / ⚠️).
 */

type Tone = 'success' | 'error' | 'info';

interface AlertItem {
  id: number;
  message: string;
  tone: Tone;
  resolve: () => void;
}

const detectTone = (msg: string): Tone => {
  const m = (msg || '').trim();
  if (m.startsWith('✅') || m.startsWith('✔') || /\bsuccess\b/i.test(m.slice(0, 40))) return 'success';
  if (m.startsWith('❌') || m.startsWith('✕') || m.startsWith('⚠️') || m.startsWith('⚠') || /\b(error|failed|cannot|denied)\b/i.test(m.slice(0, 40))) return 'error';
  return 'info';
};

const stripLeadingGlyph = (msg: string): string => {
  return (msg || '')
    .replace(/^[\s]*(✅|✔|❌|✕|⚠️|⚠|❎|❗|ℹ️|ℹ)[\s]*/u, '')
    .trim();
};

const titleFor = (tone: Tone): string => {
  if (tone === 'success') return 'Done';
  if (tone === 'error') return 'Please Note';
  return 'Notice';
};

const accent = (tone: Tone) => {
  if (tone === 'success') return { border: '#00C896', glow: 'rgba(0, 200, 150,0.25)', btnBg: '#00C896', btnBgHover: '#7ac810', btnText: '#1a1d2e' };
  if (tone === 'error') return { border: '#ef5350', glow: 'rgba(239,83,80,0.25)', btnBg: '#ef5350', btnBgHover: '#d32f2f', btnText: '#fff' };
  return { border: '#4da3ff', glow: 'rgba(77,163,255,0.25)', btnBg: '#4da3ff', btnBgHover: '#3889e0', btnText: '#fff' };
};

const icon = (tone: Tone) => {
  if (tone === 'success') return '✓';
  if (tone === 'error') return '✕';
  return 'i';
};

const GlobalAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<AlertItem[]>([]);
  const idRef = useRef(0);
  const originalAlertRef = useRef<typeof window.alert | null>(null);

  useEffect(() => {
    originalAlertRef.current = window.alert;

    // Replace the global alert — any call in the app now renders through us.
    window.alert = (msg?: any): void => {
      const text = msg === undefined || msg === null ? '' : String(msg);
      // Fire a no-op resolver; alert() is synchronous in the browser but we
      // can't replicate that in React, so treat it as async. Call sites either
      // don't await it or use it fire-and-forget — both work here.
      setQueue(prev => [
        ...prev,
        {
          id: ++idRef.current,
          message: text,
          tone: detectTone(text),
          resolve: () => {},
        },
      ]);
    };

    return () => {
      if (originalAlertRef.current) window.alert = originalAlertRef.current;
    };
  }, []);

  const current = queue[0];

  const close = () => {
    if (current) {
      try { current.resolve(); } catch {}
    }
    setQueue(prev => prev.slice(1));
  };

  const tone = current?.tone || 'info';
  const colors = accent(tone);
  const cleanMessage = current ? stripLeadingGlyph(current.message) : '';

  return (
    <>
      {children}
      <Dialog
        open={!!current}
        onClose={close}
        disableScrollLock
        disableRestoreFocus
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a1d2e',
            border: `2px solid ${colors.border}`,
            boxShadow: `0 8px 32px ${colors.glow}`,
            mx: 2,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${colors.border}26`,
              border: `2px solid ${colors.border}`,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: 34,
              lineHeight: 1,
              fontWeight: 700,
              color: colors.border,
              paddingBottom: tone === 'info' ? '4px' : 0,
            }}
          >
            {icon(tone)}
          </Box>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
            {titleFor(tone)}
          </Typography>
          <Typography
            sx={{
              color: '#cbd5e1',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
              wordBreak: 'break-word',
            }}
          >
            {cleanMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
          <Button
            onClick={close}
            variant="contained"
            autoFocus
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              minWidth: 100,
              bgcolor: colors.btnBg,
              color: colors.btnText,
              '&:hover': { bgcolor: colors.btnBgHover },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GlobalAlertProvider;
